import React, { useEffect, useState } from 'react';
import { Container, Typography, Alert, CircularProgress, Button, Box } from '@mui/material';
import { CheckCircle, Error, Warning } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/apiService';

const FeaturedPaymentStatus = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const paymentId = searchParams.get('paymentId');
    
    if (!paymentId) {
      setError('Hiányzó fizetési azonosító.');
      setLoading(false);
      return;
    }

    checkPaymentStatus(paymentId);
  }, [searchParams]);

  const checkPaymentStatus = async (paymentId) => {
    try {
      // Poll a backend-et a végleges státuszért
      const maxAttempts = 10;
      let attempts = 0;

      const pollStatus = async () => {
        attempts++;
        
        const response = await apiService.get(`/payments/status/${paymentId}`);
        
        if (response.success) {
          const status = response.data.status;
          
          // Ha végleges státusz, megállunk
          if (status === 'SUCCEEDED' || status === 'FAILED' || status === 'CANCELLED') {
            setPaymentStatus(response.data);
            setLoading(false);
            return;
          }
          
          // Ha még PENDING és van még próbálkozás
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000); // 2mp-enként újra próbáljuk
          } else {
            setError('A fizetés feldolgozása túl sokáig tart. Kérjük, ellenőrizd később.');
            setLoading(false);
          }
        }
      };

      pollStatus();

    } catch (err) {
      console.error('Payment status check error:', err);
      setError('Hiba a fizetési státusz lekérdezésekor.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Fizetés feldolgozása...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Kérjük, várj, amíg megerősítjük a fizetésedet.
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Warning sx={{ fontSize: 100, color: 'warning.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Hiba történt
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          Vissza a Dashboard-hoz
        </Button>
      </Container>
    );
  }

  // SIKERES FIZETÉS
  if (paymentStatus?.status === 'SUCCEEDED') {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 100, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Sikeres fizetés!
        </Typography>
        <Alert severity="success" sx={{ mb: 3 }}>
          A kiemelt előfizetésed aktív! Profilod már kiemelt helyen jelenik meg a listázásban.
        </Alert>
        <Box>
          <Button
            variant="contained"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Dashboard
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/provider/featured')}
          >
            Előfizetések kezelése
          </Button>
        </Box>
      </Container>
    );
  }

  // SIKERTELEN/MEGSZAKÍTOTT FIZETÉS
  return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
      <Error sx={{ fontSize: 100, color: 'error.main', mb: 2 }} />
      <Typography variant="h4" gutterBottom>
        Sikertelen fizetés
      </Typography>
      <Alert severity="error" sx={{ mb: 3 }}>
        {paymentStatus?.status === 'CANCELLED' 
          ? 'A fizetés megszakítva.' 
          : 'A fizetés sikertelen volt. Kérjük, próbáld újra.'}
      </Alert>
      <Box>
        <Button
          variant="contained"
          onClick={() => navigate('/provider/featured')}
          sx={{ mr: 2 }}
        >
          Újrapróbálás
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/dashboard')}
        >
          Vissza a Dashboard-hoz
        </Button>
      </Box>
    </Container>
  );
};

export default FeaturedPaymentStatus;